import type { BatchService } from '../../../services/batch'
import type { INestedBatchRequest } from '../../../interfaces/batch-requests'
import { readQuietReachFailure } from '../../../services/domain'

/**
 * The one part of the batch service this adapter drives, so a caller — and a test
 * — substitutes a stand-in rather than the whole service.
 */
export type OrgReachBatchService = Pick<BatchService, 'batch'>

export interface OrgReachResult {
    ok: boolean
    /** Reach per selected domain, keyed by domain id. Present only on success. */
    byDomain?: Record<number, number>
    /** True when the request was cancelled in favor of a newer one. */
    cancelled?: boolean
}

/**
 * Estimates an org segment's reach by fanning one count request out per selected
 * domain in a single batch call.
 *
 * The batch construction lives here, not in the sibling, so the component stays a
 * pure reader of the live document and this shape is testable on its own. Each
 * sub-request asks a domain's estimate-reach endpoint for a plain count — org
 * segments have no per-channel breakdown, only a per-domain one.
 *
 * A per-domain estimate is only trustworthy if every domain answered: a
 * sub-response outside 2xx, an unparseable body, or the whole batch failing all
 * resolve to an error rather than a partial total, because a silent zero for one
 * domain is indistinguishable from a domain that really reaches nobody.
 *
 * The one exception is a 422: the domain cannot target anything as configured, so
 * it really does reach nobody, and it counts as 0 while the other domains total.
 */
export async function estimateOrgReach(
    batchSvc: OrgReachBatchService,
    domainIds: number[],
    query: Record<string, unknown>,
    targetedChannels: string[],
    opts: { cancellationKey?: string } = {},
): Promise<OrgReachResult> {
    const requests: INestedBatchRequest[] = domainIds.map((domainId) => ({
        meta: { domainId },
        method: 'POST',
        relative_path: `/domains/${domainId}/segments/estimate-reach`,
        body: {
            query: { ...query, domainId },
            targetedChannels,
            returnType: 'count',
        },
    }))

    const res = await batchSvc.batch({ requests }, { cancellationKey: opts.cancellationKey })

    // Cancellation is not a failure to show — a newer request has taken over, so
    // the caller drops this result rather than rendering an error.
    if (res.cancelled) return { ok: false, cancelled: true }
    if (!res.ok || !res.data) return { ok: false }

    const byDomain: Record<number, number> = {}

    for (const sub of res.data) {
        const domainId = sub.meta?.domain_id ?? sub.meta?.domainId

        if (sub.code < 200 || sub.code >= 300) {
            if (readQuietReachFailure(domainId, sub.code, sub.body)?.countsAsZero) {
                byDomain[domainId] = 0
                continue
            }

            return { ok: false }
        }

        let reach: number
        try {
            const parsed = JSON.parse(sub.body) as { data?: { reach?: number } }
            reach = parsed?.data?.reach ?? 0
        } catch {
            return { ok: false }
        }

        byDomain[domainId] = reach
    }

    return { ok: true, byDomain }
}
